import { readStoredToken } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();
export const MESSAGES_UPDATED_EVENT = 'polarace:messages-updated';

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be configured for the frontend build');
}

function getStoredToken() {
  return readStoredToken();
}

function authHeaders(includeJson = false) {
  const token = getStoredToken();
  const headers = {};

  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

async function parseResponse(res, fallbackMessage) {
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }

  if (!res.ok) {
    throw new Error(data.detail || fallbackMessage);
  }

  return data;
}

function broadcastMessagesUpdated(detail = {}) {
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent(MESSAGES_UPDATED_EVENT, { detail }));
  }
}

function buildDisplayName(user) {
  const username = typeof user?.username === 'string' ? user.username.trim() : '';
  if (username) return username;
  const first = typeof user?.firstName === 'string' ? user.firstName.trim() : '';
  const last = typeof user?.lastName === 'string' ? user.lastName.trim() : '';
  const joined = [first, last].filter(Boolean).join(' ').trim();
  return joined || 'Unknown User';
}

function buildAvatarDataUrl(label) {
  const safeLabel = String(label || '?').trim() || '?';
  const initial = safeLabel.charAt(0).toUpperCase();
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <rect width="128" height="128" rx="64" fill="#3f7fb3"/>
      <text x="64" y="78" font-size="56" font-family="Arial, sans-serif" text-anchor="middle" fill="#ffffff">${initial}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function buildConversationUser(otherUser = {}) {
  const name = buildDisplayName(otherUser);
  return {
    id: otherUser.id || '',
    username: otherUser.username || null,
    name,
    avatar: otherUser.profileImage || buildAvatarDataUrl(name),
    isOnline: false,
  };
}

function computeTimeRemaining(updatedAt) {
  if (!updatedAt) {
    return null;
  }

  const deadline = new Date(updatedAt);
  deadline.setDate(deadline.getDate() + 7);
  const now = new Date();
  const msRemaining = deadline.getTime() - now.getTime();
  if (!Number.isFinite(msRemaining) || msRemaining <= 0) {
    return null;
  }

  const totalHours = Math.ceil(msRemaining / (1000 * 60 * 60));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return { days, hours };
}

function deriveTabType(convStatus) {
  return convStatus === 'new' || convStatus === 'pending' ? 'requests' : 'messages';
}

function deriveIsPaidRequest(convStatus) {
  return convStatus === 'new' || convStatus === 'pending';
}

function mapConversationSummary(rawConversation) {
  const unreadCount = Number(rawConversation.unreadCount || 0);

  return {
    id: rawConversation.id,
    sender: buildConversationUser(rawConversation.otherUser),
    messages: [],
    lastMessage: rawConversation.lastMessage || '',
    unread: unreadCount > 0,
    unreadCount,
    status: rawConversation.convStatus,
    canSend: Boolean(rawConversation.canSend),
    isCreator: Boolean(rawConversation.isCreator),
    isPaidRequest: deriveIsPaidRequest(rawConversation.convStatus),
    tabType: deriveTabType(rawConversation.convStatus),
    timeRemaining: rawConversation.convStatus === 'pending'
      ? computeTimeRemaining(rawConversation.updatedAt)
      : null,
    spadesEarnable: rawConversation.convStatus === 'pending' && rawConversation.isCreator ? 200 : null,
    updatedAt: rawConversation.updatedAt || null,
  };
}

function mapConversationDetail(rawConversation, summary) {
  return {
    ...(summary || {}),
    id: rawConversation.conversationId,
    sender: buildConversationUser(rawConversation.otherUser),
    messages: Array.isArray(rawConversation.messages) ? rawConversation.messages : [],
    unread: Boolean(summary?.unread),
    unreadCount: Number(summary?.unreadCount || 0),
    status: rawConversation.convStatus,
    canSend: Boolean(rawConversation.canSend),
    isCreator: Boolean(rawConversation.isCreator),
    isPaidRequest: deriveIsPaidRequest(rawConversation.convStatus),
    tabType: deriveTabType(rawConversation.convStatus),
    timeRemaining: rawConversation.convStatus === 'pending'
      ? computeTimeRemaining(summary?.updatedAt)
      : null,
    spadesEarnable: rawConversation.convStatus === 'pending' && rawConversation.isCreator ? 200 : null,
  };
}

async function messageFetch(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}/messages${path}`, {
      ...options,
      headers: {
        ...authHeaders(),
        ...(options.headers || {}),
      },
    });
  } catch {
    throw new Error('Could not reach the Polar Ace messaging service.');
  }

  return parseResponse(res, 'Messaging request failed');
}

export function normalizeSearchUser(rawUser) {
  const username = rawUser?.username || '';
  const name = buildDisplayName(rawUser);
  return {
    id: rawUser?.id || username,
    username,
    name,
    avatar: rawUser?.profileImage || buildAvatarDataUrl(name),
  };
}

export const messageApi = {
  async listConversations() {
    const data = await messageFetch('/conversations');
    return Array.isArray(data.conversations)
      ? data.conversations.map(mapConversationSummary)
      : [];
  },

  async getConversation(conversationId, summary = null) {
    const data = await messageFetch(`/conversations/${encodeURIComponent(conversationId)}`);
    return mapConversationDetail(data, summary);
  },

  async createConversation(targetUsername) {
    const data = await messageFetch('/conversations', {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ targetUsername }),
    });
    broadcastMessagesUpdated({ conversationId: data.conversationId, action: 'created' });
    return data;
  },

  async sendMessage(conversationId, content) {
    const data = await messageFetch(`/conversations/${encodeURIComponent(conversationId)}/messages`, {
      method: 'POST',
      headers: authHeaders(true),
      body: JSON.stringify({ content }),
    });
    broadcastMessagesUpdated({ conversationId, action: 'message_sent' });
    return data;
  },

  async endChat(conversationId) {
    const data = await messageFetch(`/conversations/${encodeURIComponent(conversationId)}/end-chat`, {
      method: 'PUT',
      headers: authHeaders(true),
      body: JSON.stringify({}),
    });
    broadcastMessagesUpdated({ conversationId, action: 'ended' });
    return data;
  },

  async deleteConversation(conversationId) {
    const data = await messageFetch(`/conversations/${encodeURIComponent(conversationId)}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    broadcastMessagesUpdated({ conversationId, action: 'deleted' });
    return data;
  },

  async markRead(conversationId) {
    return messageFetch(`/conversations/${encodeURIComponent(conversationId)}/read`, {
      method: 'PUT',
      headers: authHeaders(),
    });
  },

  async getUnreadCount() {
    return messageFetch('/unread-count');
  },
};
